import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Advertisement } from "@/types/advertisement";

interface AdStatisticsChartProps {
  ads: Advertisement[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export function AdStatisticsChart({ ads }: AdStatisticsChartProps) {
  // Limit to top 20 ads by performance for better chart readability
  const MAX_CHART_ITEMS = 20;
  
  // Calculate stats per ad
  const allAdStats = ads.map(ad => {
    const views = parseInt(ad.views || "0");
    const clicks = parseInt(ad.clicks || "0");
    const ctr = views > 0 ? ((clicks / views) * 100).toFixed(2) : "0";
    
    return {
      name: ad.advertiser.length > 15 ? ad.advertiser.substring(0, 15) + "..." : ad.advertiser,
      fullName: ad.advertiser,
      views,
      clicks,
      ctr: parseFloat(ctr),
      revenue: parseFloat(ad.price || "0"),
      status: ad.status,
    };
  });

  // For charts, show only top performers (by views) to avoid clutter
  const adStats = allAdStats
    .sort((a, b) => b.views - a.views)
    .slice(0, MAX_CHART_ITEMS);

  // Calculate totals from ALL ads (not just chart data)
  const totalViews = allAdStats.reduce((sum, ad) => sum + ad.views, 0);
  const totalClicks = allAdStats.reduce((sum, ad) => sum + ad.clicks, 0);
  const totalRevenue = allAdStats.reduce((sum, ad) => sum + ad.revenue, 0);
  const avgCTR = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(2) : "0";

  // Active vs Inactive ads for pie chart
  const statusData = [
    { name: "Active", value: ads.filter(ad => ad.status === "active").length },
    { name: "Inactive", value: ads.filter(ad => ad.status !== "active").length },
  ];

  // Top performing ads (by clicks)
  const topAds = [...adStats]
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalViews.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Clicks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalClicks.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Average CTR</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgCTR}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">AUD ${totalRevenue.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views vs Clicks Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Views & Clicks by Advertiser</CardTitle>
            {ads.length > MAX_CHART_ITEMS && (
              <p className="text-xs text-muted-foreground mt-1">
                상위 {MAX_CHART_ITEMS}개 광고 표시 (전체 {ads.length}개)
              </p>
            )}
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={adStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="views" fill="#8884d8" name="Views" />
                <Bar dataKey="clicks" fill="#82ca9d" name="Clicks" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* CTR Line Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Click-Through Rate (CTR) Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={adStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="ctr" stroke="#8884d8" name="CTR %" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Status Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Ad Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Advertiser</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={adStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#FFBB28" name="Revenue (AUD)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Top 5 Performing Ads (by Clicks)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topAds.map((ad, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{ad.fullName}</div>
                    <div className="text-sm text-muted-foreground">
                      {ad.views.toLocaleString()} views · {ad.clicks.toLocaleString()} clicks · {ad.ctr}% CTR
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold">AUD ${ad.revenue.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">Revenue</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
